import { useContext, useEffect, useId, useRef, useState, type KeyboardEvent } from "react";

import { cn } from "../lib/cn";
import { FieldIdContext } from "./FormControls";
import { Icon } from "./Icon";
import { Spinner } from "./Spinner";

export interface ComboboxOption {
  value: string;
  label: string;
  /** Second line under the label (e.g. a status or an email). */
  description?: string;
}

export interface ComboboxProps {
  /** The chosen value (null = nothing chosen). */
  value: string | null;
  /** Label shown for the chosen value when it isn't among the current options (e.g. loaded from the server). */
  selectedLabel?: string;
  /** Options for the current search text; the parent filters (often on the server). */
  options: ComboboxOption[];
  /** Called (debounced) with the search text whenever it changes. */
  onSearch: (text: string) => void;
  onChange: (value: string | null, option: ComboboxOption | null) => void;
  placeholder?: string;
  loading?: boolean;
  /** Screen-reader text while loading. */
  loadingLabel?: string;
  emptyLabel: string;
  clearLabel: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  "aria-label"?: string;
  "aria-invalid"?: boolean;
  /** Delay before `onSearch` runs, in ms. */
  debounce?: number;
}

/**
 * A text box that searches and picks one option (WAI-ARIA combobox with a listbox popup). Use it for long lists that
 * are filtered on the server, such as vendors or products; use `Select` for short fixed lists.
 */
export function Combobox({
  value,
  selectedLabel,
  options,
  onSearch,
  onChange,
  placeholder,
  loading = false,
  loadingLabel,
  emptyLabel,
  clearLabel,
  disabled,
  id,
  className,
  debounce = 250,
  ...aria
}: ComboboxProps) {
  const fieldId = useContext(FieldIdContext);
  const inputId = id ?? fieldId;
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [active, setActive] = useState(-1);
  const box = useRef<HTMLDivElement>(null);
  const search = useRef(onSearch);
  useEffect(() => {
    search.current = onSearch;
  }, [onSearch]);

  const chosen = options.find((option) => option.value === value);
  const shownLabel = chosen?.label ?? selectedLabel ?? "";

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => search.current(text.trim()), debounce);
    return () => clearTimeout(timer);
  }, [text, open, debounce]);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const pick = (option: ComboboxOption) => {
    onChange(option.value, option);
    setOpen(false);
    setText("");
    setActive(-1);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActive((index) => Math.min(index + 1, options.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter" && open && active >= 0 && options[active]) {
      event.preventDefault();
      pick(options[active]);
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div ref={box} className={cn("relative", className)}>
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">
          <Icon name="search" size={16} />
        </span>
        <input
          id={inputId}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
          aria-label={aria["aria-label"]}
          aria-invalid={aria["aria-invalid"]}
          autoComplete="off"
          disabled={disabled}
          placeholder={shownLabel || placeholder}
          value={open ? text : shownLabel}
          onFocus={() => {
            setOpen(true);
            setText("");
          }}
          onChange={(event) => {
            setText(event.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onKeyDown={onKeyDown}
          onBlur={(event) => {
            if (!box.current?.contains(event.relatedTarget as Node | null)) setOpen(false);
          }}
          className={
            "h-11 w-full rounded-input border border-input bg-surface pr-10 pl-10 text-sm text-foreground outline-none " +
            "placeholder:text-muted-foreground focus-visible:border-foreground focus-visible:ring-2 focus-visible:ring-brand " +
            "disabled:cursor-not-allowed disabled:opacity-60 aria-invalid:border-danger"
          }
        />
        {value !== null && !disabled && (
          <button
            type="button"
            aria-label={clearLabel}
            onClick={() => onChange(null, null)}
            className="absolute inset-y-0 right-2 flex items-center rounded-full px-1.5 text-muted-foreground hover:text-foreground"
          >
            <Icon name="close" size={16} />
          </button>
        )}
      </div>
      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-50 mt-1.5 max-h-72 w-full overflow-auto rounded-card border border-border bg-surface p-1 shadow-pop animate-fade-in"
        >
          {loading && (
            <li
              className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground"
              aria-live="polite"
            >
              <Spinner size={14} label={loadingLabel} />
            </li>
          )}
          {!loading && options.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground" aria-live="polite">
              {emptyLabel}
            </li>
          )}
          {!loading &&
            options.map((option, index) => (
              <li
                key={option.value}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={option.value === value}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(option)}
                onMouseEnter={() => setActive(index)}
                className={cn(
                  "flex cursor-pointer items-start justify-between gap-2 rounded-lg px-3 py-2 text-sm",
                  index === active && "bg-brand-soft",
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate">{option.label}</span>
                  {option.description && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {option.description}
                    </span>
                  )}
                </span>
                {option.value === value && (
                  <Icon name="check" size={16} className="mt-0.5 shrink-0" />
                )}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
